import { useAbility } from '@casl/react';
import {
  Circle,
  ControlPointDuplicate,
  Download,
  Fullscreen,
  NotInterested,
  ViewColumn,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  MRT_ColumnFiltersState,
  MRT_ExpandAllButton,
  MRT_PaginationState,
  MRT_Row,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_TableInstance,
  MRT_VisibilityState,
  createMRTColumnHelper,
} from 'material-react-table';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import { useApplicationGroups } from 'src/hooks/useApplicationGroups';
import { useApplications } from 'src/hooks/useApplications';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import { isAllSelection, isUtilityType } from 'src/utils/selection';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';
import SwitchTable from 'switch-fe-shared/components/Tables/SwitchTable';
import { ExpropApplicationOverviewRow } from 'switch-shared/modules/client/exprop/exprop.dto';
import { convertToAndFromBase } from 'switch-shared/modules/crud/utility/utility.enums';
import { OrderByDir } from 'switch-shared/types/filter';

import { ACTIONS } from '../../../../auth/utils/defineAbilityFor';
import { AbilityContext } from '../../../../context/canContext';
import { useDashboard } from '../../../../context/dashboardContext';
import pages from '../../../../router/routes';
import { FileAPIService } from '../../../../services/switch-axios-rest-api/file.service';
import { useGetApplicationMetricsQuery } from '../../../../services/switch-rest-api/dashboard.service';
import { formatCurrency, getUtilityUnits } from '../../../../utils/formatters';
import ApplicationsTableFullscreenModal from './ApplicationsTableFullscreenModal';

interface ApplicationsWidgetProps {
  pageSize?: number;
}

const ApplicationsWidget = ({ pageSize = 7 }: ApplicationsWidgetProps) => {
  const fileAPIService = new FileAPIService();

  /* Hooks & Use Effects */

  const theme = useTheme();
  const { utility } = useDashboard();
  const appGroupId = useEffectiveApplicationGroupId();
  const { applicationGroups } = useApplicationGroups();
  const appId = useEffectiveApplicationId();
  const { applications } = useApplications();
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { getApplicationScopedPath } = useAdminUrlNavigation();

  /* States */

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: 'consecutiveZero', desc: true },
  ]);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {}
  );
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const open = Boolean(anchorEl);
  const columnMenuOpen = Boolean(columnMenuAnchorEl);
  const app = applications.find((ele) => ele._id === appId);
  const appGroup = applicationGroups.find((ele) => ele._id === appGroupId);

  /* API Calls */

  const { isLoading, data, isSuccess, isFetching, isError, refetch } =
    useGetApplicationMetricsQuery(
      {
        ...(appId != null &&
          !isAllSelection(appId) && {
            applicationId: appId,
          }),
        ...(appGroupId != null &&
          !isAllSelection(appGroupId) && {
            applicationGroupId: appGroupId,
          }),
        ...(sorting.length && {
          orderByField: sorting[0].id,
          orderByValue: sorting[0].desc
            ? ('desc' as OrderByDir)
            : ('asc' as OrderByDir),
        }),
        ...(isUtilityType(utility) && { utilityType: utility }),
        page: pagination.pageIndex,
        limit: pagination.pageSize,
      },
      {
        skip: appGroupId === undefined,
      }
    );

  /* Functions & Functional Components */

  const columnHelper = createMRTColumnHelper<ExpropApplicationOverviewRow>();

  const columns = [
    columnHelper.accessor('applicationName', {
      header: 'Application',
      enableResizing: false,
      enableColumnFilter: false,
      enableColumnActions: false,
    }),
    columnHelper.accessor('meterPointName', {
      header: 'Meter Point',
      enableResizing: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      Cell: ({ cell }) => {
        return cell.getValue() ? (
          <Link
            onClick={() =>
              navigate(
                getApplicationScopedPath(
                  pages.meterpoints.name,
                  pages.meterpoints.path
                ),
                {
                  state: { meterPointId: cell.getValue() },
                }
              )
            }
            component={'button'}
            disabled={!ability.can(ACTIONS.READ, 'MeterPoints')}
            underline='hover'
            sx={{
              cursor: ability.can(ACTIONS.READ, 'MeterPoints')
                ? 'pointer'
                : 'none',
              textAlign: 'left',
            }}
          >
            {cell.getValue()}
          </Link>
        ) : (
          <Chip variant='outlined' icon={<NotInterested />} label='Unknown' />
        );
      },
    }),
    columnHelper.accessor('avg30', {
      header: 'Daily Avg',
      enableResizing: false,
      enableColumnFilter: false,
      enableSorting: false,
      enableColumnActions: false,
      Header: ({ column }) => (
        <Tooltip
          placement='top'
          title={`Daily average calculated over the past 30 days`}
        >
          <Typography fontWeight={'bold'}>{column.columnDef.header}</Typography>
        </Tooltip>
      ),
      Cell: (value) => {
        return formatCurrency(value.row.original.avg30);
      },
    }),
    columnHelper.accessor('avg24', {
      header: 'Hourly Avg',
      enableResizing: false,
      enableColumnFilter: false,
      enableSorting: false,
      enableColumnActions: false,
      Header: ({ column }) => (
        <Tooltip
          placement='top'
          title={`Hourly average calculated over the past 24 hours`}
        >
          <Typography fontWeight={'bold'}>{column.columnDef.header}</Typography>
        </Tooltip>
      ),
      Cell: (value) => {
        return formatCurrency(value.row.original.avg24);
      },
    }),
    columnHelper.accessor('avg24Diff', {
      header: 'Hourly Avg % Diff',
      enableResizing: false,
      enableColumnFilter: false,
      enableSorting: false,
      enableColumnActions: false,
      Header: ({ column }) => (
        <Tooltip
          placement='top'
          title={`The difference between the hourly average over the past 24 hours and the hour average over the past week`}
        >
          <Typography fontWeight={'bold'}>{column.columnDef.header}</Typography>
        </Tooltip>
      ),
      Cell: (value) => {
        return formatCurrency(value.row.original.avg24Diff);
      },
    }),
    columnHelper.accessor('lastSeen', {
      header: 'Last Seen',
      enableResizing: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      Header: ({ column }) => (
        <Tooltip placement='top' title={`When the meter last reported in`}>
          <Typography fontWeight={'bold'}>{column.columnDef.header}</Typography>
        </Tooltip>
      ),
      Cell: (value) => {
        return dayjs(value.row.original.lastSeen).format('HH:mm - DD/MM/YYYY');
      },
    }),
    columnHelper.accessor('dP1', {
      header: `Last Reading ${getUtilityUnits(utility, true)}`,
      enableResizing: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      Header: ({ column }) => (
        <Tooltip title={`The value of the most recent reading`} placement='top'>
          <Typography fontWeight={'bold'}>{column.columnDef.header}</Typography>
        </Tooltip>
      ),
      Cell: (value) => {
        return formatCurrency(
          value.row.original.dP1 != null
            ? convertToAndFromBase(value.row.original.dP1, utility, true)
            : 0
        );
      },
    }),
    columnHelper.accessor('consecutiveZero', {
      header: '0 Readings',
      enableResizing: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      Header: ({ column }) => (
        <Tooltip
          placement='top'
          title={`The consecutive number of times a meter has reported in 0 in a row since the last non-zero reading over the last week, indicating it is online but potentially faulty`}
        >
          <Typography fontWeight={'bold'}>{column.columnDef.header}</Typography>
        </Tooltip>
      ),
    }),
    columnHelper.accessor('meterStatus', {
      header: 'Online',
      enableResizing: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      size: 100,
      Header: ({ column }) => (
        <Tooltip
          placement='top'
          title={`Whether the meter is offline or online. A meter is online if it has been seen in the last 24 hours`}
        >
          <span>
            <Typography fontWeight={'bold'}>
              {column.columnDef.header}
            </Typography>
          </span>
        </Tooltip>
      ),
      Cell: (value) => {
        return (
          <Tooltip
            title={`${value.renderedCellValue} - ${dayjs(
              value.row.original.lastSeen
            ).format('HH:mm - DD/MM/YYYY')}`}
            placement='top'
          >
            <span>
              <Circle
                fontSize='small'
                sx={{
                  color:
                    value.renderedCellValue !== undefined
                      ? value.renderedCellValue === 'Online'
                        ? {
                            color: '#24CE25',
                            marginLeft: 1,
                          }
                        : {
                            color: 'grey',
                            marginLeft: 1,
                          }
                      : {
                          color: 'black',
                          marginLeft: 1,
                        },
                  height: theme.tokens.spacing.sm * 1.5,
                  width: theme.tokens.spacing.sm * 1.5,
                }}
              />
            </span>
          </Tooltip>
        );
      },
    }),
  ];

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColumnMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };
  const handleColumnMenuClose = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleColumnToggle = (columnId: string) => {
    setColumnVisibility((prev) => {
      const currentValue = prev[columnId as keyof typeof prev];
      // If undefined (not in object), it means column is visible by default
      // So we want to set it to false to hide it
      const newValue = currentValue === false ? true : false;
      return {
        ...prev,
        [columnId]: newValue,
      };
    });
  };

  const columnIds = columns.map((col) => ({
    id: col.accessorKey as string,
    header:
      typeof col.header === 'string' ? col.header : (col.accessorKey as string),
  }));

  const actions = (
    <>
      <Tooltip key='export-tooltip' title='Export CSV'>
        <IconButton
          key='export'
          disabled={appGroupId == null}
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          size='small'
        >
          <Download />
        </IconButton>
      </Tooltip>
      <Menu
        key='export-menu'
        id='basic-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem
          onClick={() => {
            fileAPIService.fetchCSV(
              'exprop',
              {
                ...(appId != null &&
                  !isAllSelection(appId) && {
                    applicationId: appId,
                  }),
                ...(appGroupId != null &&
                  !isAllSelection(appGroupId) && {
                    applicationGroupId: appGroupId,
                  }),
              },
              `metrics-${
                isAllSelection(appId) || appId == null
                  ? appGroup?.name
                  : app?.name
              }-${dayjs().format('YYYYMMDD-HHmm')}`
            );
            handleClose();
          }}
        >
          Export All Data
        </MenuItem>
        <MenuItem
          onClick={() => {
            fileAPIService.fetchCSV(
              'exprop',
              {
                ...(appId != null &&
                  !isAllSelection(appId) && {
                    applicationId: appId,
                  }),
                ...(appGroupId != null &&
                  !isAllSelection(appGroupId) && {
                    applicationGroupId: appGroupId,
                  }),
                ...(sorting.length && {
                  orderByField: sorting[0].id,
                }),
                ...(sorting.length && {
                  orderByValue: sorting[0].desc
                    ? ('desc' as OrderByDir)
                    : ('asc' as OrderByDir),
                }),
                utilityType: utility,
                page: pagination.pageIndex,
                limit: pagination.pageSize,
              },
              `metrics-${
                isAllSelection(appId) || appId == null
                  ? appGroup?.name
                  : app?.name
              }-${dayjs().format('YYYYMMDD-HHmm')}`
            );
            handleClose();
          }}
        >
          Export Data with filters
        </MenuItem>
      </Menu>
      <Tooltip
        key='columns-tooltip'
        placement='bottom'
        title={`Show/Hide columns`}
      >
        <IconButton
          key='columns'
          aria-controls={columnMenuOpen ? 'column-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={columnMenuOpen ? 'true' : undefined}
          onClick={handleColumnMenuClick}
          size='small'
        >
          <ViewColumn />
        </IconButton>
      </Tooltip>
      <Menu
        key='column-menu'
        id='column-menu'
        anchorEl={columnMenuAnchorEl}
        open={columnMenuOpen}
        onClose={handleColumnMenuClose}
        MenuListProps={{
          'aria-labelledby': 'column-button',
        }}
      >
        {columnIds.map((column) => (
          <MenuItem
            key={column.id}
            sx={{ height: theme.tokens.spacing.xl * 1.3 }}
            onClick={() => handleColumnToggle(column.id)}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    columnVisibility[
                      column.id as keyof typeof columnVisibility
                    ] !== false
                  }
                />
              }
              label={column.header}
              sx={{ pointerEvents: 'none', width: '100%' }}
            />
          </MenuItem>
        ))}
      </Menu>
      <Tooltip key='fullscreen-tooltip' title='Fullscreen'>
        <IconButton
          key='fullscreen'
          onClick={(e) => {
            e.stopPropagation();
            setIsFullscreen(true);
          }}
          size='small'
        >
          <Fullscreen />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <>
      <WidgetCard
        title='Meter Points Table'
        subheader='Metrics'
        avatar={
          <Box
            sx={{
              padding: `${theme.tokens.spacing.sm}px`,
              height: `${theme.tokens.spacing.sm * 5}px`,
              width: `${theme.tokens.spacing.sm * 5}px`,
              mx: `-${theme.tokens.spacing.sm}px`,
              borderRadius: theme.tokens.borderRadius.full,
              background: theme.tokens.colors.card,
              boxShadow: theme.tokens.shadows.sm,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.primary.main,
            }}
          >
            <ControlPointDuplicate />
          </Box>
        }
        action={actions}
        cardContentProps={{
          sx: {
            display: 'flex',
            flex: 1,
            height: '80%',
            width: '100%',
            flexDirection: 'column',
            background: theme.colors.neutral.grey[20],
            borderTop: `1px solid ${theme.tokens.extendedColors.borders.light}`,
            paddingBottom: '0px !important',
            px: 0,
          },
        }}
      >
        <SwitchTable<ExpropApplicationOverviewRow>
          columns={columns}
          data={data?.data ?? []}
          rowCount={data?.totalRecords ?? 0}
          isError={isError}
          title={'Application Metrics'}
          isFetching={isFetching}
          isLoading={isLoading}
          manualPagination
          muiTablePaperProps={{
            sx: { maxHeight: 'auto' },
          }}
          muiPaginationProps={{ rowsPerPageOptions: [pageSize, 25, 50, 100] }}
          state={{
            pagination,
            sorting,
            rowSelection,
            columnFilters,
            columnVisibility,
            globalFilter,
          }}
          onGlobalFilterChange={setGlobalFilter}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          onColumnVisibilityChange={setColumnVisibility}
          onRowSelectionChange={setRowSelection}
          onColumnFiltersChange={setColumnFilters}
          enableRowActions={false}
          enableTopToolbar={false}
          enableColumnFilters={false}
          enableFullScreenToggle={false}
          displayColumnDefOptions={{
            'mrt-row-expand': {
              Header: ({
                table,
              }: {
                table: MRT_TableInstance<ExpropApplicationOverviewRow>;
              }) => (
                <Stack direction='row' alignItems='center'>
                  <MRT_ExpandAllButton table={table} />
                  <Box>Application</Box>
                </Stack>
              ),
              GroupedCell: ({
                row,
                table,
              }: {
                row: MRT_Row<ExpropApplicationOverviewRow>;
                table: MRT_TableInstance<ExpropApplicationOverviewRow>;
              }) => {
                // eslint-disable-next-line react/prop-types
                const { grouping } = table.getState();
                return row.getValue(grouping[grouping.length - 1]);
              },
              enableResizing: true,
              muiTableBodyCellProps: ({ row }) => ({
                sx: (theme) => ({
                  color:
                    row.depth === 0
                      ? theme.palette.primary.main
                      : row.depth === 1
                        ? theme.palette.secondary.main
                        : undefined,
                }),
              }),
              size: 150,
            },
          }}
        />
      </WidgetCard>

      <ApplicationsTableFullscreenModal
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        rowCount={data?.totalRecords ?? 0}
        utility={utility}
        appId={appId}
        appGroupId={appGroupId}
        appName={app?.name}
        appGroupName={appGroup?.name}
      />
    </>
  );
};

export default ApplicationsWidget;
